import { gql } from '@apollo/client';
import {
  AbstractComparatorApiService,
  AddressApiSuccessResponse,
  AddressData,
  CalculateParameters,
  Comparison,
  CustormerBenefitItem,
  IComparatorApiServiceParameters,
  IGraphProvider,
  basicHomeProduct,
  basicHomeSupplier,
} from '../../..';
import { LegacyOrderResponse } from '../../../../../helpers';
import { getCalculateSessionId } from '../../../comparator-graph-legacy-api';
import {
  HomeProduct,
  ProductSupplier,
  ProductSupplierFeature,
  ProductsWithSuppliers,
} from '../../../graphProduct';
import {
  FeaturesConnection,
  MasterAgreementStructure,
  OrderResult,
  OrderStatusResult,
} from '../../../home';
import {
  IProduct,
  QuoteResponse,
  basicHomeIProduct,
  getUspsBySellingPoints,
} from '../../../product';
import {
  calculateSolarPanelsQuery,
  createLead,
  getPanelEstimationQuery,
  getProducts,
  getProductsForSupplierQuery,
  getProductsWithSuppliers,
  getStreetViewImageQuery,
  getSupplierAdditionalCostsQuery,
  getVatValues,
  sendEmailToCustomerQuerySolarPanels,
  sendInactivityEmailToCustomerQuerySolarPanels,
} from '../../queries';
import calculationInputQuery from '../../queries/solar-panels/calculationInputQuery';
import { ApolloProvider } from '../apolloProvider';
import { validateAddressCommon } from '../commonGraphQLAPI';
import { SolarPanelsQueryProvider } from './solarPanelsQueryProvider';
import {
  CalculateSolarPanelsPayload,
  CommonTechnical,
  SolarPanels_PolicyDetails,
  TriggerEventPayload,
} from './types';

export class GraphSolarPanelsApi extends AbstractComparatorApiService {
  private client;
  private queryProvider: SolarPanelsQueryProvider;
  private readonly graphProvider: IGraphProvider;
  constructor(params: IComparatorApiServiceParameters) {
    super(params);
    this.queryProvider = new SolarPanelsQueryProvider();
    this.graphProvider = new ApolloProvider(params);
  }

  private generateUUID(): string {
    let d = new Date().getTime(); //Timestamp
    let d2 =
      (typeof performance !== 'undefined' &&
        performance.now &&
        performance.now() * 1000) ||
      0; //Time in microseconds since page-load or 0 if unsupported
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
      /[xy]/g,
      function (c) {
        let r = self.crypto.getRandomValues(new Uint32Array(1))[0] % 17;
        if (d > 0) {
          //Use timestamp until depleted
          r = (d + r) % 16 | 0;
          d = Math.floor(d / 16);
        } else {
          //Use microseconds since page-load if supported
          r = (d2 + r) % 16 | 0;
          d2 = Math.floor(d2 / 16);
        }
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
      }
    );
  }

  setClient(client) {
    this.client = client;
  }

  getClient() {
    return this.client;
  }

  getCustomerBenefits(): Promise<CustormerBenefitItem[]> {
    throw new Error('Method not implemented.');
  }

  getQuotePrefill(
    accessToken: string,
    swiftUserId: string,
    swiftProductId: string,
    productType: string
  ): Promise<QuoteResponse | false> {
    console.log(swiftUserId, swiftProductId, productType, accessToken);
    throw new Error('Method not implemented');
  }

  validateAddress(
    postcode: string,
    houseNumber: string,
    houseNumberAddition?: string
  ): Promise<AddressApiSuccessResponse> {
    const variables = this.queryProvider.validateAddressVariables(
      postcode,
      parseInt(houseNumber),
      houseNumberAddition
    );

    return validateAddressCommon(this.graphProvider, variables);
  }

  getAddressData(
    postalCode: string,
    houseNumber: string,
    houseNumberAddition?: string
  ): Promise<AddressData> {
    console.log('getAddressData', postalCode, houseNumber, houseNumberAddition);
    throw new Error('Method not implemented.');
  }

  getHomeFeaturesDetails(
    productId: number | number[],
    after: string | null
  ): Promise<FeaturesConnection> {
    console.log('getHomeFeaturesDetails', productId, after);
    throw new Error('getHomeFeaturesDetails method not implemented.');
  }

  calculateHomeContentPackage(
    calculateParameters: CalculateParameters
  ): Promise<MasterAgreementStructure> {
    console.log('calculateHomeContentPackage', calculateParameters);
    throw new Error('Method not implemented.');
  }

  buildCharacteristics = (features: ProductSupplierFeature[]) =>
    features
      ? features.map((feature) => {
          return {
            label: feature.name,
            value: feature.value,
          };
        })
      : [];

  sendEmailToCustomer(
    comparisonId: string | number,
    email: string,
    productType: string,
    inactivity: boolean
  ): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const variables = this.queryProvider.validateEmailVariables(
        comparisonId,
        email,
        productType
      );

      const query = inactivity
        ? sendInactivityEmailToCustomerQuerySolarPanels
        : sendEmailToCustomerQuerySolarPanels;

      const newClientLink = `${process.env.KIZI_PUBLIC_DOMAIN}/events/graphql`;

      this.graphProvider
        .mutate<TriggerEventPayload>(
          gql(query),
          variables,
          'triggerEvent',
          undefined,
          newClientLink
        )
        .then((data: TriggerEventPayload) => {
          if (data.success) {
            resolve('Email sent successfully');
          } else {
            reject('Email could not be sent');
          }
        })
        .catch((err) => {
          console.error(err);
          reject('Email could not be sent');
        });
    });
  }

  getCalculationInput(
    transactionReference: string
  ): Promise<MasterAgreementStructure> {
    console.log('getCalculationInput', transactionReference);
    throw new Error('Method not implemented.');
  }

  orderStatus(orderId: string): Promise<OrderStatusResult> {
    console.log('orderStatus', orderId);
    throw new Error('Method not implemented.');
  }

  getOrderDefinition(
    product: IProduct,
    productType: 'cars' | 'energy' | 'energyAuction'
  ): Promise<LegacyOrderResponse['data']> {
    console.log('getOrderDefinition', product, productType);
    throw new Error('Method not implemented.');
  }

  getComparison(id: string): Promise<Comparison> {
    const newClientLink = `${process.env.KIZI_PUBLIC_DOMAIN}/solarpanels/graphql`;
    const sharedClientLink = `${process.env.KIZI_PUBLIC_DOMAIN}/shared/services/graphql`;
    return new Promise<Comparison>((resolve, reject) => {
      this.graphProvider
        .query<any>(
          gql(calculationInputQuery),
          { comparisonId: id },
          'calculationInput',
          undefined,
          newClientLink
        )
        .then(async (data: any) => {
          if (!data.calculationInput) {
            let address, streetViewImage;
            try {
              address = await this.validateAddress(
                data.policyPolicyDetails[0].objectBuildings[0].addresses[0]
                  .postalCode,
                data.policyPolicyDetails[0].objectBuildings[0].addresses[0]
                  .houseNumber,
                data.policyPolicyDetails[0].objectBuildings[0].addresses[0]
                  .houseNumberAddition || ''
              );
              if (address) {
                const { preAssignedUrl } = (await this.getStreetViewImage(
                  address?.postcode,
                  Number(address?.houseNumber),
                  data.policyPolicyDetails[0].objectBuildings[0].addresses?.[0]
                    ?.houseNumberAddition || null,
                  getCalculateSessionId(),
                  sharedClientLink
                )) as { preAssignedUrl: string };
                if (preAssignedUrl) {
                  streetViewImage = preAssignedUrl;
                }
              }
            } catch {
              console.warn('Address is invalid');
            }

            const roofLocationMapping = this.queryProvider.roofLocationMapping;
            const roofConstructionType =
              data.policyPolicyDetails[0].objectBuildings[0]
                .roofConstructionType;
            const roofType =
              roofConstructionType === 'V01' ? 'platdak' : 'schuindak';
            const roofOrientation =
              data.policyPolicyDetails[0].objectBuildings[0]?.roofOrientation;
            const roofLocation =
              roofConstructionType === 'V02'
                ? Object.keys(roofLocationMapping).find(
                    (key) => roofLocationMapping[key] === roofOrientation
                  )
                : null;
            const roofMaterial =
              roofConstructionType === 'V01' ? 'bitumen' : 'dakpannen';

            const additionalCosts =
              data.policyPolicyDetails[0].salesDockComparisons[0]
                .additionalCosts;
            const roofSurface =
              additionalCosts.length > 0
                ? (Number(
                    additionalCosts.find((el) =>
                      el.description.includes('oplevering')
                    )?.description[0]
                  ) || 0) + 1
                : 1;
            const meterBox = additionalCosts?.some(
              (el) => el.description === 'Vervangen meterkast'
            )
              ? 'stoppen'
              : 'schakelaars';

            let productsRes, dutchVatRes, supplierAdditionalCostsRes;
            try {
              productsRes = await this.getSolarPanelsProducts(
                getCalculateSessionId(),
                newClientLink
              );
              dutchVatRes = await this.getVatValues(sharedClientLink);

              supplierAdditionalCostsRes =
                await this.getSupplierAdditionalCosts(
                  getCalculateSessionId(),
                  newClientLink
                );
            } catch {
              console.warn("Couldn't get products");
            }

            const criteria = {
              criteria: {
                ...address,
                hometype: { startWidget: 'true' },
                postcode:
                  data.policyPolicyDetails[0].objectBuildings[0].addresses?.[0]
                    ?.postalCode,
                housenumber:
                  data.policyPolicyDetails[0].objectBuildings[0].addresses?.[0]
                    ?.houseNumber,
                houseNumberAddition:
                  data.policyPolicyDetails[0].objectBuildings[0].addresses?.[0]
                    ?.houseNumberAddition || '',
                streetViewImage,
                electricityusage:
                  data.policyPolicyDetails[0].salesDockComparisons[0]
                    .usageElectricity,
                roofType,
                roofLocation,
                roofMaterial,
                roofSurface,
                roofShade: data.policyPolicyDetails[0].salesDockComparisons[0]
                  .hasShadowFormation
                  ? '1'
                  : '2',
                meterBox,
                numberOfSolarPanels:
                  data.policyPolicyDetails[0].salesDockComparisons[0]
                    .numberOfPanels,
                products: JSON.stringify(productsRes || []),
                vatValue: dutchVatRes.dutchVat || '',
                supplierAdditionalCosts: JSON.stringify(
                  supplierAdditionalCostsRes || []
                ),
                filters: JSON.stringify({
                  color: [],
                  wattPeak: [],
                  inverter: [],
                  providerName: [],
                }),
              },
            };

            resolve(criteria);
          } else {
            reject('Incorrect Response!');
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  getProducts(
    searchCriteria: object,
    selectedFilters: object
  ): Promise<IProduct[]> {
    const solarPanelsClientLink = `${process.env.KIZI_PUBLIC_DOMAIN}/solarpanels/graphql`;
    const productClientLink = `${process.env.KIZI_PUBLIC_DOMAIN}/product/graphql`;

    return new Promise<IProduct[]>((resolve, reject) => {
      const variables = this.queryProvider.validateSolarPanelsVariables(
        searchCriteria,
        selectedFilters,
        this.credentials,
        this.generateUUID()
      );

      let packages: SolarPanels_PolicyDetails[] = [];
      let products: HomeProduct[] = [];
      let suppliers: ProductSupplier[] = [];
      let commonTechnical = [{}];

      this.graphProvider
        .mutate<CalculateSolarPanelsPayload>(
          gql(calculateSolarPanelsQuery),
          variables,
          'calculateSolarPanels',
          undefined,
          solarPanelsClientLink
        )
        .then((data) => {
          if (
            data.solarPanelCalculationResult &&
            data.solarPanelCalculationResult.policyPolicyDetails
          ) {
            packages = data.solarPanelCalculationResult.policyPolicyDetails;
            const result = data.solarPanelCalculationResult;

            if (Array.isArray(result.commonTechnical)) {
              commonTechnical = result.commonTechnical as CommonTechnical[];
            }

            const productsWithSuppliersQueryVars =
              this.queryProvider.validateSuppliersVariables(result);

            this.graphProvider
              .query<ProductsWithSuppliers>(
                gql(getProductsWithSuppliers),
                productsWithSuppliersQueryVars,
                'productsWithSuppliers',
                undefined,
                productClientLink
              )
              .then(async (productsWithSuppliers) => {
                products = productsWithSuppliers.products;
                suppliers = productsWithSuppliers.suppliers;

                const formatted = this.formatSolarPanelsProduct(
                  this.credentials,
                  packages,
                  products,
                  suppliers,
                  commonTechnical,
                  searchCriteria
                );

                const createLeadQueryVars =
                  this.queryProvider.validateCreateLeadVariables(
                    result,
                    searchCriteria
                  );

                const createLeadSession = sessionStorage.getItem('createLead');
                if (!createLeadSession) {
                  this.graphProvider
                    .mutate<any>(
                      gql(createLead),
                      createLeadQueryVars,
                      'createLead',
                      undefined,
                      solarPanelsClientLink
                    )
                    .then((data) => {
                      sessionStorage.setItem(
                        'createLead',
                        data?.createLeadResult?.successful
                      );
                    })
                    .catch((error) => {
                      console.log(error);
                      reject(error);
                    });
                }

                resolve(formatted);
              })
              .catch((error) => {
                console.log(error);
                reject(error);
              });
          } else {
            reject('No products returned');
          }
        })
        .catch((error) => {
          console.log(error);
          reject('No products returned');
        });
    });
  }

  formatSolarPanelsProduct(
    credentials: any,
    packages: SolarPanels_PolicyDetails[],
    products: HomeProduct[],
    suppliers: ProductSupplier[],
    commonTechnical: CommonTechnical[],
    searchCriteria: object
  ): IProduct[] {
    return packages
      .filter((item) => item?.pricingTotals?.[0]?.netInvestment !== null)
      .map((policyDetails) => {
        const {
          // companyNameDescription,
          coverageHomes,
          customProperties,
          // documents,
          // externalSourceName,
          // monthlyExtraPricing,
          // onetimeExtraPricing,
          // orderFormId,
          pricingTotals,
          productCode,
          productDescription,
          // refKey,
          savingBenefits,
          supplierExternalId,
          // uniqueSellingPoint,
          yearlyGenerationInKwh,
        } = policyDetails;
        const product: HomeProduct = {
          ...basicHomeProduct,
          ...products.find((p) => p.krn === productCode),
        };
        const supplier: ProductSupplier = {
          ...basicHomeSupplier,
          ...suppliers.find((s) => s.krn === customProperties?.supplierKrn),
        };
        const {
          affiliateUrl,
          insuranceCardUrl,
          orderType,
          productType,
          termsAndConditionsUrl,
          testScores,
        } = product;

        const {
          krn,
          about,
          advantages,
          disadvantages,
          features,
          howItWorks,
          name,
          nameLogoUrl,
          sellingPoints,
          sustainabilityScore,
          taskDivisions,
        } = supplier;

        let visibleAffiliateUrl = '';

        if (orderType === 'AFFILIATE') {
          visibleAffiliateUrl = affiliateUrl;
        }

        const panel = coverageHomes?.find(
          (el) => el.refTypeDescription === 'pv_panel'
        );
        let inverter;
        const pv_inverter = coverageHomes?.find(
          (el) => el.refTypeDescription === 'pv_inverter'
        )?.externalSourceName;

        if (new RegExp(/string/, 'gmi')?.test(pv_inverter || '')) {
          inverter = 'Serieel (String)';
        } else if (new RegExp(/parallel/, 'gmi')?.test(pv_inverter || '')) {
          inverter = 'Parallel';
        } else if (new RegExp(/micro/, 'gmi')?.test(pv_inverter || '')) {
          inverter = 'Micro omvormer';
        } else if (new RegExp(/centrale/, 'gmi')?.test(pv_inverter || '')) {
          inverter = 'Centrale omvormer';
        } else if (new RegExp(/solar edge/, 'gmi')?.test(pv_inverter || '')) {
          inverter = 'Power optimizers';
        }

        const additionalCostPrices = this.queryProvider.calculateProductCosts(
          searchCriteria,
          supplierExternalId
        );

        return {
          ...basicHomeIProduct,
          additionalCostPrices,
          affiliateUrl: visibleAffiliateUrl,
          commonTechnical: [
            {
              senderId: credentials.clientId,
              sessionId: '',
              transactionReference:
                commonTechnical[0].transactionReference || '',
            },
          ],
          bigProductFeature: {
            ...basicHomeIProduct.bigProductFeature,
            decimals: 0,
            label: 'Eigen risico',
            hideIfZero: true,
            variant: 'priceMedium',
            value: {
              month: 0,
              year: 0,
            },
          },
          coverageHomes,
          characteristics: this.buildCharacteristics(features || []),
          id: `${productCode}`,
          image: nameLogoUrl,
          orderType,
          priceInfo: {
            ...basicHomeIProduct.priceInfo,
            initial: pricingTotals?.[0].netInvestment,
            total: pricingTotals?.[0].netInvestment,
          },
          productCode: productCode || '',
          productType: productType || '',
          termsAndConditionsUrl,
          testScores,
          prosAndCons: {
            cons: disadvantages?.map((el) => el.description),
            pros: advantages?.map((el) => el.description),
          },
          provider: {
            ...basicHomeIProduct.provider,
            description: about,
            howItWorks,
            insuranceCardUrl,
            label: name + ' ' + productDescription,
            name,
            sellingPoints,
            sustainabilityScore: sustainabilityScore
              ? `${sustainabilityScore[0].score}`
              : '',
            taskDivisions,
            termsAndConditionsUrl,
            id: krn,
          },
          purchasableProduct: orderType !== 'NOT_ORDERABLE',
          testScore: testScores?.[0]?.score || 0,
          usps: getUspsBySellingPoints(sellingPoints),
          subscriptionName: productDescription || '',
          supplierExternalId,
          color:
            panel?.solarPanels?.[0]?.color === 'Full Black'
              ? 'Toon alleen Volledig zwart'
              : null,
          wattPeak: `${panel?.solarPanels?.[0]?.wattPeak} WP`,
          inverter,
          savingBenefits,
          netInvestment: pricingTotals?.[0].netInvestment,
          type: 'solarPanels',
          policyPolicyDetails: [policyDetails],
          supplierName: name,
          yearlyGenerationInKwh,
        };
      });
  }

  orderAffiliateContentPackage(
    transactionReference: string,
    productCode: string
  ): Promise<OrderResult> {
    console.log(transactionReference, productCode);
    throw new Error('Method not implemented.');
  }

  getStreetViewImage(
    zipCode: string,
    houseNumber: number,
    houseNumberAddition: string | null,
    correlationId: string,
    newClientLink?: string
  ) {
    const variables = {
      address: {
        zipCode,
        houseNumber,
        houseNumberAddition,
      },
      correlationId,
    };
    return new Promise((resolve, reject) => {
      this.graphProvider
        .query(
          getStreetViewImageQuery,
          variables,
          'streetViewLocation',
          undefined,
          newClientLink
        )
        .then((res) => {
          resolve(res);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  getPanelEstimation(request: any, sessionId: string, newClientLink?: string) {
    const variables = { request, sessionId };
    return new Promise((resolve, reject) => {
      this.graphProvider
        .query(
          getPanelEstimationQuery,
          variables,
          'panelEstimation',
          undefined,
          newClientLink
        )
        .then((res) => {
          resolve(res);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  getSolarPanelsProducts(sessionId: string, newClientLink?: string) {
    const variables = { sessionId };
    return new Promise((resolve, reject) => {
      this.graphProvider
        .query(getProducts, variables, 'products', undefined, newClientLink)
        .then((res) => {
          resolve(res);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  getVatValues(newClientLink?: string) {
    return new Promise((resolve, reject) => {
      this.graphProvider
        .query(getVatValues, {}, 'vatValues', undefined, newClientLink)
        .then((res) => {
          resolve(res);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  getSupplierAdditionalCosts(sessionId: string, newClientLink?: string) {
    const variables = { sessionId };
    return new Promise((resolve, reject) => {
      this.graphProvider
        .query(
          getSupplierAdditionalCostsQuery,
          variables,
          'supplierAdditionalCosts',
          undefined,
          newClientLink
        )
        .then((res) => {
          resolve(res);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  getProductsForSupplier(
    supplierId: number,
    sessionId: string,
    newClientLink?: string
  ) {
    const variables = { supplierId, sessionId };
    return new Promise((resolve, reject) => {
      this.graphProvider
        .query(
          getProductsForSupplierQuery,
          variables,
          'productsForSupplier',
          undefined,
          newClientLink
        )
        .then((res) => {
          resolve(res);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  checkMembershipStatus(): Promise<CustormerBenefitItem[]> {
    throw new Error('Method not implemented.');
  }
}
